import * as React from 'react';
import { getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';
import { billingService } from '@/services/billingService';
import type { Invoice } from '@/types/payment';

interface UseInvoiceDownloadReturn {
  handleDownload: (invoice: Invoice) => Promise<void>;
  handleDownloadAll: (invoices: Invoice[]) => Promise<void>;
}

/** Handles customer subscription invoice downloads through the billing API. */
export function useInvoiceDownload(): UseInvoiceDownloadReturn {
  const { addToast } = useToast();
  const handleDownload = React.useCallback(
    async (invoice: Invoice): Promise<void> => {
      if (!invoice.documentAvailable) return;
      try {
        const result = await billingService.getInvoiceDocument(invoice.id);
        window.open(result.url, '_blank', 'noopener,noreferrer');
      } catch (error) {
        addToast({
          title: 'Invoice unavailable',
          subtitle: getErrorMessage(error),
          type: 'error',
        });
      }
    },
    [addToast]
  );

  const handleDownloadAll = React.useCallback(
    async (invoices: Invoice[]): Promise<void> => {
      await Promise.allSettled(invoices.map((invoice) => handleDownload(invoice)));
    },
    [handleDownload]
  );

  return { handleDownload, handleDownloadAll };
}
